(ns minorodata.components.pipeline-editor.search
  (:require
   ["react" :as react]
   [clojure.string :as str]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.dynamic-docs.sidebar :refer [search-with-popover]]
   [minorodata.components.pipeline-editor.utils :refer [navigate-table-tab
                                                        tab-icon]]
   [minorodata.components.spinner.core :refer [with-text]]
   [re-frame.core :as re-frame]
   [ui.typography :as typography]))

;; Entity types openable as pipeline-editor tabs. Tables are sandbox-only — the
;; kioku live global search doesn't index them, but the pipeline-editor sandbox
;; service does.
(defn search-types [sandbox?]
  (cond-> #{"transform" "extract"}
    sandbox? (conj "table")))

(def min-query-length 3)

(def dispatch-search
  (debounce
   (fn [query sandbox?]
     ;; Sandbox mode hits the pipeline-editor service's twin search endpoint so
     ;; results reflect the sandbox; live mode hits the kioku global search.
     (dispatch [(if sandbox? :sandbox.api/search :global-search.api/fetch)
                {:query query :types (search-types sandbox?)}]))
   300))

(defn split-qualified
  "A table result's id is its fully-qualified name (`catalog.schema.table`).
  Split it into the short table name and its `catalog.schema` parent."
  [qualified]
  (let [parts (str/split (or qualified "") #"\.")]
    {:table  (last parts)
     :parent (str/join "." (butlast parts))}))

(defn result-title [{:keys [type id name]}]
  (if (= type "table") (:table (split-qualified id)) name))

(defn result-subtitle
  "Muted breadcrumb under the result name — extract's parent source, transform's
  group, table's catalog.schema. Prefers the name the search response carries
  (kioku provides it), and falls back to resolving it by id from already-loaded
  data (the pipeline-editor sandbox service omits the source name)."
  [{:keys [type id metadata]} groups sources]
  (case type
    "transform" (or (not-empty (:group-name metadata))
                    (get groups (:group-id metadata)))
    "extract"   (or (not-empty (:source-name metadata))
                    (get sources (:source-id metadata)))
    "table"     (:parent (split-qualified id))
    nil))

(defn result-item [{:keys [type] :as result} {:keys [on-click groups sources]}]
  [:button {:class    "flex items-center gap-2 text-left py-2 px-2 border-b last:border-b-0 hover:opacity-60 transition-opacity"
            :on-click #(on-click result)}
   [tab-icon {:type type}]
   [:div {:class "min-w-0 flex flex-col"}
    [typography/h5 {:class "leading-3 truncate"} (result-title result)]
    (when-let [subtitle (result-subtitle result groups sources)]
      [typography/small {:class "text-kleene-text-secondary leading-3 truncate"} subtitle])]])

(defn search' [{:keys [align results loading? sandbox? groups sources]}]
  (let [[query set-query] (react/useState "")
        [open set-open]   (react/useState false)
        on-change   (fn [e]
                      (let [value (some-> e .-target .-value)]
                        (set-query value)
                        (when (>= (count value) min-query-length)
                          (dispatch-search value sandbox?))))
        on-navigate (fn [{:keys [type id] :as result}]
                      ;; Tables aren't handled by the shared open-in-PE handler
                      ;; (kioku never returns them); open the table tab directly.
                      (if (= type "table")
                        (let [{:keys [table parent]} (split-qualified id)]
                          (navigate-table-tab id parent table))
                        (dispatch [:global-search/open-in-pipeline-editor result]))
                      (set-open false))
        entities    (if sandbox? "transforms, tables and extracts" "transforms and extracts")]
    [search-with-popover {:placeholder    (str "Search " entities)
                          :class          "overflow-y-auto w-2xl max-h-(--radix-popper-available-height) max-w-none"
                          :open           open
                          :align          align
                          :set-open       set-open
                          :on-open-change (fn [next]
                                            (set-open next)
                                            (when-not next
                                              (dispatch [:global-search/clear])))
                          :on-change      on-change}
     [:div {:class "flex flex-col min-w-2xs"}
      (cond
        (< (count query) min-query-length)
        [:div {:class "text-center text-kleene-text-secondary"}
         (str "Type at least " min-query-length " characters")]

        (and loading? (empty? results))
        [with-text]

        (empty? results)
        [:div {:class "text-center text-kleene-text-secondary"}
         (str "No " entities " found")]

        :else
        (for [{:keys [type id] :as result} results]
          ^{:key (str type "-" id)}
          [result-item result {:on-click on-navigate
                               :groups   groups
                               :sources  sources}]))]]))

(defn search [{:keys [align]}]
  (let [sandbox? @(re-frame/subscribe [:sandbox.subs/enabled?])
        loading? (or @(re-frame/subscribe [:mold-http/loading? :global-search.api/fetch])
                     @(re-frame/subscribe [:mold-http/loading? :sandbox.api/search]))
        results  (->> @(re-frame/subscribe [:global-search/results])
                      (filter (comp (search-types sandbox?) :type)))
        ;; id->name fallbacks for the subtitle, resolved from already-loaded data
        ;; (covers names the search response omits, e.g. sandbox extract sources).
        groups   (into {} (map (juxt :pipeline-group/id :pipeline-group/name))
                       @(re-frame/subscribe [:pipeline-editor.subs/transform-groups]))
        sources  (into {} (map (juxt :source-id :name))
                       @(re-frame/subscribe [:pipeline-editor.subs/sources]))]
    [:f> search' {:align    align
                  :results  results
                  :loading? loading?
                  :sandbox? sandbox?
                  :groups   groups
                  :sources  sources}]))
